import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, IconButton, Button, Alert, FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';

import SectionCard from './SectionCard';
import { TableCard, GroupLabel, tableHeadCellSx } from '../tabLayout';
import { Fact, FactGrid, OnOffPill, QuietNote, numSx } from './sectionParts';
import { formatMoneyFull } from '../../reports/finance/chartConfig';
import { useAuth } from '../../../context/AuthContext';
import { AdmissionCycle, RefundPolicy, RefundSlab } from '../../../types/admissions';

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * Refund policy — ordered slabs computed against the paid admission-fee
 * invoice at withdrawal time (see redesign §5 Journey 6). Rule of thumb:
 *   • slabs sorted by beforeDays descending ("30+ days before classes-begin")
 *   • security-deposit flag refunds that head regardless of the slab
 *
 * A sample-amount preview makes the math concrete — operators often set
 * a 100% / 50% / 0% schedule and want to confirm what a parent would
 * actually get back for a Rs 50,000 fee.
 */

const DEFAULT_SAMPLE = 50000;

const RefundPolicySection: React.FC<Props> = ({ cycle, canEdit, onSave }) => {
  const { currency } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<RefundPolicy>({
    slabs: [],
    securityDepositAlwaysRefundable: true,
  });
  const [sampleAmount, setSampleAmount] = useState<number>(DEFAULT_SAMPLE);

  useEffect(() => {
    if (editing) {
      setDraft({
        slabs: cycle.refundPolicy?.slabs || [],
        securityDepositAlwaysRefundable: cycle.refundPolicy?.securityDepositAlwaysRefundable ?? true,
      });
    }
  }, [editing, cycle.refundPolicy]);

  const current = editing ? draft : cycle.refundPolicy || { slabs: [], securityDepositAlwaysRefundable: true };

  // Sort the slabs descending by beforeDays for the display + the preview
  // calculation. Keeps the "most time, most refund" story at the top.
  const sortedSlabs: RefundSlab[] = useMemo(
    () => [...(current.slabs || [])].sort((a, b) => b.beforeDays - a.beforeDays),
    [current.slabs],
  );

  const addSlab = () => {
    setDraft(prev => ({ ...prev, slabs: [...prev.slabs, { beforeDays: 0, percentage: 0 }] }));
  };
  const updateSlab = (idx: number, patch: Partial<RefundSlab>) => {
    setDraft(prev => ({
      ...prev,
      slabs: prev.slabs.map((s, i) => i === idx ? { ...s, ...patch } : s),
    }));
  };
  const removeSlab = (idx: number) => {
    setDraft(prev => ({ ...prev, slabs: prev.slabs.filter((_, i) => i !== idx) }));
  };

  const hasInvalid = draft.slabs.some(
    s => s.beforeDays < 0 || s.percentage < 0 || s.percentage > 100,
  );

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ refundPolicy: draft });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // Preview: given a sample amount, what % back for each days-before scenario?
  const previewRow = (daysBefore: number): { pct: number; amount: number } => {
    // Find the first slab whose threshold is <= daysBefore; slabs are stored
    // descending so the top match is correct.
    const match = sortedSlabs.find(s => daysBefore >= s.beforeDays);
    const pct = match ? match.percentage : 0;
    return { pct, amount: Math.round((pct / 100) * sampleAmount) };
  };

  return (
    <SectionCard
      title="Refund policy"
      description="How much of the admission fee is returned if a student withdraws, based on how long before classes begin."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setDraft({
        slabs: cycle.refundPolicy?.slabs || [],
        securityDepositAlwaysRefundable: cycle.refundPolicy?.securityDepositAlwaysRefundable ?? true,
      })}
      saving={saving}
      canSave={!hasInvalid}
      headerActions={
        <Button size="small" startIcon={<Add fontSize="small" />} onClick={addSlab}>
          Add slab
        </Button>
      }
    >
      <Stack spacing={3}>
        {/* Slabs */}
        <Box>
          <GroupLabel>Refund schedule</GroupLabel>
          {sortedSlabs.length === 0 ? (
            <QuietNote>No refund schedule — withdrawals are not refunded, apart from the security deposit setting below.</QuietNote>
          ) : (
            <TableCard>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>If the student withdraws</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Refund</TableCell>
                    {editing && <TableCell sx={tableHeadCellSx} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(editing ? draft.slabs : sortedSlabs).map((slab, idx) => (
                    <TableRow key={`slab-${idx}`} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                      <TableCell>
                        {editing ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">at least</Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={slab.beforeDays}
                              onChange={(e) => updateSlab(idx, { beforeDays: Math.max(0, parseInt(e.target.value || '0', 10) || 0) })}
                              inputProps={{ min: 0, step: 1 }}
                              sx={{ width: 90 }}
                            />
                            <Typography variant="body2" color="text.secondary">days before classes begin</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2">
                            {slab.beforeDays === 0
                              ? 'Any time before classes begin'
                              : `At least ${slab.beforeDays} ${slab.beforeDays === 1 ? 'day' : 'days'} before classes begin`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editing ? (
                          <TextField
                            type="number"
                            size="small"
                            value={slab.percentage}
                            onChange={(e) => updateSlab(idx, { percentage: Math.min(100, Math.max(0, parseFloat(e.target.value || '0') || 0)) })}
                            inputProps={{ min: 0, max: 100, step: 1, style: { textAlign: 'right' } }}
                            sx={{ width: 100 }}
                            InputProps={{ endAdornment: <Typography variant="body2" color="text.secondary">%</Typography> }}
                          />
                        ) : (
                          <Typography variant="body2" fontWeight={600} sx={numSx} color={slab.percentage ? 'text.primary' : 'text.disabled'}>
                            {slab.percentage}%
                          </Typography>
                        )}
                      </TableCell>
                      {editing && (
                        <TableCell align="right" padding="none" sx={{ pr: 1 }}>
                          <Tooltip title="Remove slab">
                            <IconButton size="small" color="error" onClick={() => removeSlab(idx)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableCard>
          )}
        </Box>

        {/* Security deposit flag */}
        {editing ? (
          <FormControlLabel
            control={
              <Switch
                checked={!!draft.securityDepositAlwaysRefundable}
                onChange={(e) => setDraft({ ...draft, securityDepositAlwaysRefundable: e.target.checked })}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Security deposit always refunded
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When on, fees marked refundable are returned in full whatever the schedule says.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="body2" fontWeight={500}>Security deposit always refunded in full</Typography>
              <Typography variant="caption" color="text.secondary">
                {current.securityDepositAlwaysRefundable
                  ? 'Refundable fees are returned in full, whatever the schedule says.'
                  : 'Refundable fees follow the schedule above.'}
              </Typography>
            </Box>
            <OnOffPill on={!!current.securityDepositAlwaysRefundable} />
          </Stack>
        )}

        {/* Worked example */}
        {!editing && sortedSlabs.length > 0 && (
          <Box sx={{ pt: 2.5, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>Example</Typography>
                <Typography variant="caption" color="text.secondary">What a parent gets back on a fee of this amount.</Typography>
              </Box>
              <TextField
                size="small"
                type="number"
                label={`Fee amount (${currency})`}
                value={sampleAmount}
                onChange={(e) => setSampleAmount(Math.max(0, parseFloat(e.target.value || '0') || 0))}
                sx={{ width: 180 }}
                inputProps={{ min: 0, step: 1000 }}
              />
            </Stack>
            <FactGrid columns={5}>
              {[60, 30, 14, 7, 0].map(days => {
                const { pct, amount } = previewRow(days);
                return (
                  <Fact key={days} label={days === 0 ? 'Withdrawn on the day' : `${days}+ days before`}>
                    {formatMoneyFull(amount, currency)}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>{pct}%</Typography>
                  </Fact>
                );
              })}
            </FactGrid>
          </Box>
        )}

        {editing && hasInvalid && (
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              Days before must be non-negative; refund percentage must be between 0 and 100.
            </Typography>
          </Alert>
        )}
      </Stack>
    </SectionCard>
  );
};

export default RefundPolicySection;
